import config from "#src/config";
import { AuthenticatedContextActiveAuthVersionType } from "#src/contexts/AuthenticatedContext";
import {
  addBreadcrumb,
  captureConsoleIntegration,
  captureException,
  flush,
  getCurrentScope,
  init,
  reactRouterV5BrowserTracingIntegration,
  withScope,
  type SeverityLevel,
} from "@sentry/react";
import {
  DomainAdapterError,
  UserType,
  type UserReturnType,
} from "@validereinc/domain";
import {
  BaseError,
  HTTPRequestError,
  HTTPResponseError,
} from "@validereinc/utilities";
import {
  ExceptionContextKeys,
  LifecycleEvent,
  LifecycleEventCategory,
  isPrimitiveString,
  isPrimitiveStringArray,
} from "./exception.helpers";

/**
 * Boot the exception handler utilities
 */
const setup = ({
  dsn,
  environment,
  version,
}: {
  dsn: string;
  environment: string;
  version: string;
  isDebuggingEnabled?: boolean;
}) => {
  init({
    dsn,
    environment,
    release: version,
    integrations: [
      reactRouterV5BrowserTracingIntegration({
        history,
        enableInp: true,
      }),
      captureConsoleIntegration({
        levels: ["warn"],
      }),
    ],
    // IMPROVE: we should enable distributed tracing again when our BE APIs support it https://docs.sentry.io/platforms/javascript/guides/react/performance/
    tracesSampleRate: 0,
    // IMPROVE: we should enable distributed tracing again when our BE APIs support it https://docs.sentry.io/platforms/javascript/guides/react/distributed-tracing/
    tracePropagationTargets: [],
    beforeBreadcrumb: (breadcrumb, hint) => {
      switch (breadcrumb.category) {
        case "ui.click": {
          const { target } = hint?.event ?? {};

          breadcrumb.message =
            target instanceof HTMLElement
              ? `tag:${target.tagName}|role:${target.role ?? "unknown"}|text:${target.textContent && target.textContent.length > 25 ? `${target.textContent.substring(0, 25)}` : (target.textContent ?? target.innerText ?? "unknown")}|label:${target.ariaLabel ?? (target.title || "unknown")}`
              : breadcrumb.message;
          break;
        }
      }

      return breadcrumb;
    },
    beforeSend: (event, hint) => {
      const exception = hint.originalException;

      // drop browser-level fetch failures — these are caused by the user's
      // network environment (connectivity loss, navigation away, ad blockers,
      // etc.) and are not actionable
      if (
        exception instanceof HTTPRequestError &&
        exception._causeName === "TypeError"
      ) {
        return null;
      }

      // drop Auth0's "Login required" error — this fires on every cold load
      // from an unauthenticated or expired session and is expected behavior
      if (
        exception instanceof Error &&
        exception.message === "Login required"
      ) {
        return null;
      }

      // drop 401 and 404 HTTP response errors — 401s trigger a redirect to
      // login and 404s are handled with fallback UI; neither is actionable
      if (
        exception instanceof HTTPResponseError &&
        exception.status &&
        [401, 404].includes(exception.status)
      ) {
        return null;
      }

      // drop Axios network errors from the legacy service layer — same root
      // cause as HTTPRequestError TypeError (connectivity loss, etc.)
      if (
        exception instanceof Error &&
        "code" in exception &&
        exception.code === "ERR_NETWORK"
      ) {
        return null;
      }

      // attempt to fingerprint the exception
      try {
        if (exception instanceof DomainAdapterError) {
          event.fingerprint = [
            "{{ default }}",
            "{{ transaction }}",
            String(exception.code),
            String(exception.method),
            ...(exception.adapterCalls?.length
              ? [JSON.stringify(exception.adapterCalls)]
              : []),
          ];
        } else if (
          exception instanceof HTTPRequestError ||
          exception instanceof HTTPResponseError
        ) {
          event.fingerprint = [
            "{{ default }}",
            "{{ transaction }}",
            String(exception.code),
            String(exception.method),
          ];
        } else if (exception instanceof BaseError) {
          event.fingerprint = [
            "{{ default }}",
            "{{ transaction }}",
            String(exception.code),
          ];
        }

        return event;
      } catch (error) {
        // do nothing
        return event;
      }
    },
  });
};

/**
 * Report an exception to an external logging system. (Sentry in our case)
 *
 * Exceptions are grouped together in Sentry using grouping rules - one concept
 * in grouping is the idea of "fingerprinting" an exception. The rules for that
 * are defined in Sentry but also saved here in a file `exception.fingerprint` as
 * a reference.
 * @see {@link https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/fingerprinting/}
 * @see {@link https://docs.sentry.io/concepts/data-management/event-grouping/fingerprint-rules/}
 *
 * @param err - the error instance
 * @param severity - the severity of the error (default: "error")
 * HTTPResponseError of status 401, 403, or 404 will be reported as "warning"
 * always. Development (NODE_ENV = "development") logs are reported as "debug" always.
 * @param sourceComponent - the component stringified stack trace or the name of
 * the component
 * @param hint - optional developer-friendly contextual details for additional
 * information about the error
 * @returns void
 */
const reportException = (
  /** the error instance */
  err: unknown,
  /** the severity of the error */
  severity?: SeverityLevel,
  {
    sourceComponent,
    hint,
  }: {
    /** the component stringified stack trace or the name of the component */
    sourceComponent?: string;
    /** optional dev-friendly contextual details about the source of the error */
    hint?: string;
  } = {}
) => {
  withScope(function (scope) {
    if (hint || sourceComponent) {
      scope.setContext("Source", {
        hint,
        sourceComponent,
      });
    }

    let severityFromError: SeverityLevel | undefined;

    if (err instanceof BaseError) {
      const isHTTPError =
        err instanceof HTTPResponseError ||
        err instanceof HTTPRequestError ||
        err instanceof DomainAdapterError;

      severityFromError = err.severity;

      // If error is "Forbidden (403) report it as a warning:
      if (isHTTPError) {
        if (!severityFromError && err.status && err.status === 403) {
          severityFromError = "warning";
        }
      }

      scope.setContext("CustomErrorClassProperties", {
        ...BaseError.serialize(err),
        explanation: err.getExplanation(),
        referenceURL: err.getReferenceURL(),
      });
      scope.setTags({
        [ExceptionContextKeys.isValidereDefined]: err._isValidereDefined,
        [ExceptionContextKeys.exceptionName]: err.name,
        ...(err._causeName
          ? { [ExceptionContextKeys.exceptionCauseName]: err._causeName }
          : {}),
        [ExceptionContextKeys.exceptionCode]: err.nameWithCode,
        [ExceptionContextKeys.exceptionExplanation]: err.getExplanation(),
        ...(isHTTPError
          ? {
              [ExceptionContextKeys.networkRequestEndpoint]: err.endpoint,
              [ExceptionContextKeys.networkRequestMethod]: err.method,
              [ExceptionContextKeys.networkResponseStatus]: err.status,
              [ExceptionContextKeys.networkRequestContentType]:
                err.requestContentType,
              ...(err instanceof HTTPResponseError
                ? {
                    [ExceptionContextKeys.networkResponseContentType]:
                      err.responseContentType,
                  }
                : {}),
            }
          : {}),
        ...(err instanceof DomainAdapterError
          ? {
              [ExceptionContextKeys.domainAdapterCalls]:
                err.adapterCalls?.join(", ") ?? "n/a",
            }
          : {}),
      });
    }

    // precedence order:
    // 1. severity passed in
    // 2. severity from error
    // 3. default to "error"
    scope.setLevel(severity || severityFromError || "error");
    captureException(err);
  });
};

const registerLifecycleEvent = (data: LifecycleEvent) => {
  addBreadcrumb(
    {
      ...data,
      timestamp: new Date().getTime() / 1000,
    },
    data.data
  );
};

const registerAuthenticatedSessionHandshake = (handshakeData: {
  /** the authenticated user's ID */
  id?: string;
  /** name of the user */
  name?: string;
  /** email of the user */
  email: string;
  /** is the authenticated user's email verified? */
  isEmailVerified: boolean;
  /** seconds since UNIX epoch */
  sessionStart: number;
}) => {
  if (!handshakeData.id) return;

  const scope = getCurrentScope();

  scope.setUser({
    ...scope.getUser(),
    id: handshakeData.id,
    name: handshakeData.name,
    email: handshakeData.email,
    isEmailVerified: handshakeData.isEmailVerified,
  });
  scope.setTag(ExceptionContextKeys.sessionStart, handshakeData.sessionStart);
};

const registerAuthenticatedSession = (
  authenticatedUserData: Partial<
    Pick<
      UserReturnType,
      "id" | "name" | "email" | "status" | "quicksight" | "company_id"
    >
  > & {
    /** the authentication service(s) in play for the authenticated user */
    activeAuthVersion: AuthenticatedContextActiveAuthVersionType;
    /** the preferred timezone setting on the user's account */
    timezonePreferred?: UserType["timezone"];
  }
) => {
  const scope = getCurrentScope();

  scope.setUser({
    id: authenticatedUserData.id,
    ip_address: "{{auto}}",
    company_id: authenticatedUserData.company_id,
    name: authenticatedUserData.name,
    email: authenticatedUserData.email,
  });
  scope.setTags({
    [ExceptionContextKeys.timezone_preferred]:
      authenticatedUserData.timezonePreferred,
    [ExceptionContextKeys.timezone_actual]:
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    [ExceptionContextKeys.locale_preferred]: config.locale,
    [ExceptionContextKeys.locale_actual]:
      navigator?.language ??
      Intl.DateTimeFormat().resolvedOptions().locale ??
      config.locale,
    [ExceptionContextKeys.company]: authenticatedUserData.company_id,
    [ExceptionContextKeys.hasQuicksightIntegration]:
      authenticatedUserData.quicksight,
    [ExceptionContextKeys.activeAuthVersion]:
      isPrimitiveString(authenticatedUserData.activeAuthVersion) ||
      isPrimitiveStringArray(authenticatedUserData.activeAuthVersion)
        ? isPrimitiveStringArray(authenticatedUserData.activeAuthVersion)
          ? authenticatedUserData.activeAuthVersion.join("-")
          : authenticatedUserData.activeAuthVersion
        : "unknown",
  });
};

/**
 * Get the details of the current registered authenticated session
 * @returns the user session details and any associated metadata that was registered
 */
const getAuthenticatedSession = (): {
  user: ReturnType<ReturnType<typeof getCurrentScope>["getUser"]>;
  tags: ReturnType<ReturnType<typeof getCurrentScope>["getScopeData"]>["tags"];
  contexts: ReturnType<
    ReturnType<typeof getCurrentScope>["getScopeData"]
  >["contexts"];
} => {
  const scope = getCurrentScope();

  return {
    user: scope.getUser(),
    tags: scope.getScopeData().tags,
    contexts: scope.getScopeData().contexts,
  };
};

/**
 * Shutdown the exception handler utilities
 */
const teardown = () => {
  const scope = getCurrentScope();

  return flush(3000).then(() => {
    scope.setUser(null);
    scope.clear();
  });
};

export const ExceptionUtils: {
  setup: typeof setup;
  teardown: typeof teardown;
  reportException: typeof reportException;
  registerLifecycleEvent: typeof registerLifecycleEvent;
  registerAuthenticatedSession: typeof registerAuthenticatedSession;
  registerAuthenticatedSessionHandshake: typeof registerAuthenticatedSessionHandshake;
  getAuthenticatedSession: typeof getAuthenticatedSession;
  ExceptionContextKeys: typeof ExceptionContextKeys;
  LifecycleEventCategory: typeof LifecycleEventCategory;
} = {
  setup,
  teardown,
  reportException,
  registerLifecycleEvent,
  registerAuthenticatedSession,
  registerAuthenticatedSessionHandshake,
  getAuthenticatedSession,
  ExceptionContextKeys,
  LifecycleEventCategory,
};
